import { useMemo, useState } from 'react';
import { Button } from '@src/design-system/button';
import { useNavigate } from 'react-router-dom';
import { createRoute, PATHS } from '@src/core/router/paths';
import {
  AlertTriangle,
  RefreshCcw,
  ArrowLeft,
  Home,
  ClipboardCopy,
  ChevronDown,
} from 'lucide-react';
import { logger } from '@shared/utils/logger';

function ErrorPage({ error }: { error?: Error }) {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  const errorDetailsText = useMemo(() => {
    const lines = [
      `Message: ${error?.message ?? 'Unknown error'}`,
      `Location: ${window.location?.pathname ?? ''}`,
      `Time: ${new Date().toISOString()}`,
      error?.stack ? `\nStack:\n${error.stack}` : undefined,
    ].filter(Boolean);
    return lines.join('\n');
  }, [error]);

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleHomepage = () => {
    navigate(createRoute(PATHS.PROGRAMS));
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(errorDetailsText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (copyError) {
      logger.warn(
        'Failed to copy error details to clipboard',
        copyError as unknown
      );
    }
  };

  return (
    <div className='min-h-screen bg-primary-50 flex items-center justify-center px-4 py-10'>
      <div className='w-full max-w-2xl bg-white border border-pale-border rounded-xl shadow-sm p-6 sm:p-8 text-center'>
        <div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600'>
          <AlertTriangle className='h-7 w-7' />
        </div>
        <h1 className='text-2xl font-semibold text-gray-900'>
          Something went wrong
        </h1>
        <p className='mt-2 text-gray-700'>
          An unexpected error occurred. You can try again, go back, or head to
          the homepage.
        </p>

        {error?.message && (
          <div className='mt-4 text-sm text-gray-900 bg-gray-50 border border-pale-border rounded-lg px-4 py-3 text-left'>
            <span className='font-medium text-red-500'> Error: </span>{' '}
            {error.message}
          </div>
        )}

        <div className='mt-6 flex flex-col sm:flex-row gap-3 justify-center'>
          <Button onClick={handleReload}>
            <RefreshCcw className='w-4 h-4' />
            <span>Retry</span>
          </Button>
          <Button
            onClick={handleGoBack}
            variant='primaryOutline'
          >
            <ArrowLeft className='w-4 h-4' />
            <span>Go Back</span>
          </Button>
          <Button
            onClick={handleHomepage}
            variant='primaryOutline'
          >
            <Home className='w-4 h-4' />
            <span>Homepage</span>
          </Button>
          <Button
            onClick={handleCopy}
            variant='secondary'
          >
            <ClipboardCopy className='w-4 h-4' />
            <span>{copied ? 'Copied!' : 'Copy details'}</span>
          </Button>
        </div>

        <div className='mt-4'>
          <button
            type='button'
            onClick={() => setShowDetails((v) => !v)}
            className='inline-flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900'
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`}
            />
            <span>
              {showDetails
                ? 'Hide technical details'
                : 'Show technical details'}
            </span>
          </button>
          {showDetails && (
            <pre className='mt-3 max-h-64 overflow-auto rounded-lg bg-gray-50 p-3 text-left text-xs text-gray-800 border border-pale-border whitespace-pre-wrap'>
              {errorDetailsText}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

export default ErrorPage;
